import React, { memo, useMemo, useReducer, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Modal } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import Welcome from './Welcome';
import EnterPhone from './EnterPhone';
import EnterCode from './EnterCode';
import Contact from './Contact';
import ForceManagerWithLogoLight from './icons/ForceManagerWithLogoLight';
import { generateCode, verifyCode, setPhone } from 'lib/services/2fa';

import styles from './styles';
const useStyles = createUseStyles(styles, 'Forms');

const phonePattern = new RegExp(/^[0-9\b]+$/);

const initialState = {
    start: false,
    country: null,
    phoneNumber: null,
    code: null,
    focus: 0,
    modifyPhone: false,
    remainingVerifyAttempts: null,
    wait: null,
    error: null,
    isSavingPhone: false,
    isVerifyingCode: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                countriesList: action.countriesList,
                country: action.country,
                phoneNumber: action.phoneNumber,
                error: action.error,
            };
        case 'setCodeData':
            return {
                ...state,
                modifyPhone: false,
                remainingVerifyAttempts: 2,
                isSavingPhone: false,
                wait: null,
                code: [],
                focus: 0,
                error: null,
            };
        case 'setStart':
            return { ...state, start: true };
        case 'setCode':
            return { ...state, code: action.value };
        case 'setFocus':
            return { ...state, focus: action.value };
        case 'setCountry':
            return { ...state, country: action.value };
        case 'setPhoneNumber':
            return { ...state, phoneNumber: action.value, error: null };
        case 'setModifyPhone':
            return { ...state, modifyPhone: action.value, error: null };
        case 'setInvalidCode':
            return {
                ...state,
                error: action.error,
                remainingVerifyAttempts: state.remainingVerifyAttempts - 1,
                isVerifyingCode: false,
            };
        case 'setInvalidPhone':
            return {
                ...state,
                error: action.value,
                isSavingPhone: false,
            };
        case 'setSavingPhone':
            return {
                ...state,
                isSavingPhone: true,
                error: null,
            };
        case 'setVerifyingCode':
            return {
                ...state,
                isVerifyingCode: true,
            };
        case 'setWait':
            return {
                ...state,
                wait: action.value,
            };
        case 'onClose':
            return {
                ...state,
                countriesList: null,
            };
        default:
            throw new Error('No action provided');
    }
}

function TwoFA({
    isOpen,
    onClose,
    onFinish,
    isFirstTime,
    isPhoneEditable,
    prefixId,
    phoneNumber,
    prefixList,
    credentials = {},
}) {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);
    const buttonRef = useRef(null);

    const _generateCode = useCallback(
        () =>
            generateCode()
                .then(({ data }) => {
                    if (data?.remainingAttempts < 0) {
                        dispatch({ type: 'setWait', value: data.lastAttempt });
                    } else {
                        dispatch({ type: 'setCodeData' });
                    }
                })
                .catch((err) => {
                    onClose(err);
                }),
        [onClose],
    );

    useEffect(() => {
        if (isOpen && prefixList && !state.countriesList) {
            const countriesList = prefixList.map((el) => ({
                value: el.id,
                label: el.country,
                prefix: el.prefix,
            }));
            const error = phonePattern.test(phoneNumber) ? null : t('cfm_label_invalid_phone');
            dispatch({
                type: 'init',
                phoneNumber,
                country: countriesList.find((el) => el.value === prefixId),
                countriesList,
                error,
            });
            !isFirstTime && _generateCode();
        }
        if (!isOpen && state.countriesList) {
            dispatch({ type: 'onClose' });
        }
    }, [
        _generateCode,
        isOpen,
        prefixId,
        isFirstTime,
        phoneNumber,
        prefixList,
        state.country,
        state.phoneNumber,
        state.countriesList,
        t,
    ]);

    const setCountry = useCallback((value) => {
        dispatch({ type: 'setCountry', value });
    }, []);

    const setPhoneNumber = useCallback((value) => {
        dispatch({ type: 'setPhoneNumber', value });
    }, []);

    const setModifyPhone = useCallback((value) => {
        dispatch({ type: 'setModifyPhone', value });
    }, []);

    const setCode = useCallback((value) => {
        dispatch({ type: 'setCode', value });
    }, []);

    const setFocus = useCallback((value) => {
        dispatch({ type: 'setFocus', value });
    }, []);

    const onClickStart = useCallback(() => {
        dispatch({ type: 'setStart' });
    }, []);

    const onClickSend = useCallback(() => {
        if (phonePattern.test(state.phoneNumber)) {
            dispatch({ type: 'setSavingPhone' });
            setPhone({ prefixId: state.country?.value, phoneNumber: state.phoneNumber })
                .then(_generateCode)
                .catch((err) => {
                    if (err?.response?.data?.error === 'duplicated phone number') {
                        dispatch({
                            type: 'setInvalidPhone',
                            value: t('cfm_label_duplicated_phone'),
                        });
                    } else if (err?.response?.data?.error === 'prefix not found') {
                        dispatch({
                            type: 'setInvalidPhone',
                            value: t('cfm_label_invalid_phone'),
                        });
                    } else {
                        onClose(err);
                    }
                });
        } else {
            dispatch({ type: 'setInvalidPhone', value: t('cfm_label_invalid_phone') });
        }
    }, [_generateCode, onClose, state.country, state.phoneNumber, t]);

    const onClickVerify = useCallback(() => {
        dispatch({ type: 'setVerifyingCode' });
        const code = state.code.join('');
        verifyCode({ code, ...credentials })
            .then(({ data }) => onFinish({ credentials, ...data }))
            .catch((err) => {
                const setInvalidCode = (error) => {
                    dispatch({ type: 'setInvalidCode', error });
                    setCode([]);
                    setFocus(0);
                };
                if (err?.response?.data?.error === 'invalid code') {
                    if (!state.wait && state.remainingVerifyAttempts === 0) {
                        _generateCode().then(() =>
                            setInvalidCode(t('cfm_error_2fa_wrong_code_with_sms')),
                        );
                    } else {
                        setInvalidCode(t('cfm_error_2fa_wrong_code_simple'));
                    }
                } else {
                    onClose(err);
                }
            });
    }, [
        state.code,
        state.wait,
        state.remainingVerifyAttempts,
        credentials,
        onFinish,
        setCode,
        setFocus,
        _generateCode,
        t,
        onClose,
    ]);

    const onClickDone = useCallback(() => setModifyPhone(false), [setModifyPhone]);

    const onClickClose = useCallback(() => onClose(), [onClose]);

    const hiddenPhone = useMemo(() => {
        return state.phoneNumber
            ?.split('')
            .map((c, i) => {
                if (i > state.phoneNumber.length - 4) {
                    return c;
                }
                return '*';
            })
            .join('');
    }, [state.phoneNumber]);

    const screen = useMemo(() => {
        if (isFirstTime && !state.start) {
            return {
                key: '2fa-welcome',
                ready: true,
                title: <ForceManagerWithLogoLight />,
                content: <Welcome onClickStart={onClickStart} />,
                size: 'medium',
                useHeader: true,
                onRequestClose: onClickClose,
            };
        }
        if (
            (isPhoneEditable && state.start && !state.code) ||
            (isPhoneEditable && state.modifyPhone && !state.wait)
        ) {
            return {
                key: '2fa-enter-phone',
                ready: true,
                title: t('cfm_label_2fa_enter_phone_number'),
                content: (
                    <EnterPhone
                        countriesList={state.countriesList}
                        country={state.country}
                        error={state.error}
                        setCountry={setCountry}
                        phoneNumber={state.phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                    />
                ),
                size: 'medium',
                useHeader: true,
                onRequestClose: onClickClose,
                onCancel: onClickClose,
                onConfirm: onClickSend,
                confirmText: t('cfm_label_2fa_send_code'),
                cancelText: t('cfm_action_close'),
                overrides: {
                    confirmButton: {
                        isLoading: state.isSavingPhone,
                        isDisabled: !(state.country && state.phoneNumber),
                    },
                },
            };
        }
        if (!isPhoneEditable && state.modifyPhone) {
            return {
                key: 'action-done-2',
                ready: true,
                content: <Contact />,
                size: 'small',
                useHeader: false,
                onConfirm: onClickDone,
                confirmText: t('cfm_action_done_2'),
            };
        }
        if (state.code || state.wait) {
            return {
                key: '2fa-enter-code',
                ready: true,
                title: t('cfm_label_2fa_enter_code'),
                content: (
                    <EnterCode
                        code={state.code}
                        focus={state.focus}
                        wait={state.wait}
                        hiddenPhone={hiddenPhone}
                        isFirstTime={isFirstTime}
                        buttonRef={buttonRef}
                        error={state.error}
                        setCode={setCode}
                        setFocus={setFocus}
                        onClickModifyPhone={setModifyPhone}
                        generateCode={_generateCode}
                    />
                ),
                size: 'small',
                useHeader: true,
                onRequestClose: onClickClose,
                onCancel: onClickClose,
                onConfirm: onClickVerify,
                confirmText: t('cfm_action_2fa_verify_code'),
                cancelText: t('cfm_action_close'),
                overrides: {
                    confirmButton: {
                        ref: (ref) => (buttonRef.current = ref),
                        isLoading: state.isVerifyingCode,
                        isDisabled:
                            state.wait ||
                            state.code.length !== 6 ||
                            state.code.reduce(
                                (noUndef, item) => noUndef || item === undefined,
                                false,
                            ),
                    },
                },
            };
        }
        return { ready: false };
    }, [
        isFirstTime,
        isPhoneEditable,
        state.start,
        state.code,
        state.modifyPhone,
        state.wait,
        state.countriesList,
        state.country,
        state.error,
        state.phoneNumber,
        state.isSavingPhone,
        state.focus,
        state.isVerifyingCode,
        hiddenPhone,
        onClickStart,
        onClickClose,
        onClickVerify,
        onClickSend,
        onClickDone,
        setCountry,
        setPhoneNumber,
        setCode,
        setFocus,
        setModifyPhone,
        _generateCode,
        t,
    ]);

    return (
        <Modal
            key={screen.key}
            size={screen.size}
            title={screen.title}
            useHeader={screen.useHeader}
            isOpen={isOpen && screen.ready}
            className={classes.root}
            onRequestClose={screen.onRequestClose}
            onCancel={screen.onCancel}
            onConfirm={screen.onConfirm}
            confirmText={screen.confirmText}
            cancelText={screen.cancelText}
            overrides={screen.overrides}
        >
            {screen.content}
        </Modal>
    );
}

TwoFA.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onFinish: PropTypes.func,
    isFirstTime: PropTypes.bool,
    isPhoneEditable: PropTypes.bool,
    prefixId: PropTypes.number,
    phoneNumber: PropTypes.string,
    credentials: PropTypes.object,
    prefixList: PropTypes.array,
};

export default memo(TwoFA);
